import { createFileRoute } from '@tanstack/react-router'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'

import { Button } from '@/components/ui/button'

import { useMemo, useState } from 'react'
import { RxPlus } from 'react-icons/rx'
import { columns, Habit } from '@/components/habits/habitsColumns'
import HabitDialog from '@/components/habits/habitDialog'
import DeleteAlert from '@/components/DeleteAlert'
import { trpc } from '@/lib/trpc/client'
import {
  useHasPermission,
  useOrg,
  useUserId,
} from '@/lib/states/session/hooks'
import { errorToast } from '@/lib/utils/ToastUtils'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import { canDeleteOrgLibraryItem } from '@/lib/libraryAccess'

export const Route = createFileRoute('/_auth/_dashboard/habits')({
  component: HabitsPage,
})

function HabitsPage() {
  const org = useOrg()
  const userId = useUserId()
  const canManageLibrary = useHasPermission('library.edit')
  const tableColumns = useMemo(
    () => columns({ userId, canManageLibrary }),
    [userId, canManageLibrary],
  )
  const [open, setOpen] = useState(false)
  const [habit, setHabit] = useState<Habit>()
  const [habitToDelete, setHabitToDelete] = useState<Habit>()

  const {
    data: habits,
    error,
    isLoading,
  } = trpc.habits.list.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })
  const utils = trpc.useUtils()
  const deleteHabit = trpc.habits.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.habits.list, (old) =>
        removeFromList(old, variables.id),
      )
      setHabitToDelete(undefined)
    },
    onError: (mutationError) => {
      errorToast(mutationError)
    },
  })

  if (error) {
    console.error('error fetching habits', error)
  }

  const handleOnClickEdit = (selected: Habit) => {
    if (open) {
      return
    }
    setHabit(selected)
    setOpen(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setHabit(undefined)
    setOpen(nextOpen)
  }

  const handleHabitDeleted = (selected: Habit) => {
    if (!canDeleteOrgLibraryItem(selected, { userId, canManageLibrary })) {
      return
    }
    setHabitToDelete(selected)
  }

  return (
    <>
      <DeleteAlert
        title={`Delete habit`}
        description={`Are you sure you want to delete this habit? This action cannot be undone.`}
        open={habitToDelete !== undefined}
        onCancel={() => setHabitToDelete(undefined)}
        onDelete={() => {
          if (!habitToDelete?.id) {
            setHabitToDelete(undefined)
            return
          }
          deleteHabit.mutate({ id: habitToDelete.id })
        }}
      />
      <HabitDialog open={open} onOpenChange={handleOpenChange} habit={habit} />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Habits</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={habits ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={handleHabitDeleted}
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
            />
          </div>
        </div>
      </div>
    </>
  )
}
